<template>
	<AsiDialogFormLayout v-if="model !== null"
	                     :icon="icons.mail"
	                     :title="$t('cart.bulkOffer.title')"
	                     :open="open"
	                     :loading="loading"
	                     :valid="valid"
	                     :errors="errors"
	                     :save-label="$t('cart.bulkOffer.requestButton')"
	                     :save-icon="icons.mail"
	                     :save-disabled="loading"
	                     @save="requestSave"
	                     @cancel="requestCancel"
	                     max-width="58rem"
	                     unwrapped
	                     scrollable>
		<v-card-text class="pa-0">
			<v-form ref="form" v-model="valid">
				<div class="pa-6">
					<p class="body-1 mb-4">{{ $t('cart.bulkOffer.text', {threshold: bulkOfferThreshold}) }}</p>
					<p class="body-2 mb-0">{{ $t('cart.bulkOffer.formHint') }}</p>
				</div>
				<v-divider/>
				<div class="pa-6">
					<h3 class="subtitle-1 mb-3">{{ $t('cart.bulkOffer.articleDetails') }}</h3>
					<v-simple-table dense>
						<tbody>
							<tr>
								<th class="text-left">{{ $t('item.singular') }}</th>
								<td>{{ itemName }}</td>
							</tr>
							<tr>
								<th class="text-left">{{ $t('cart.bulkOffer.itemNumber') }}</th>
								<td>{{ position.item.itemNumber }}</td>
							</tr>
							<tr v-if="position.item.alternativeItemNumber !== null">
								<th class="text-left">{{ $t('cart.bulkOffer.alternativeItemNumber') }}</th>
								<td>{{ position.item.alternativeItemNumber }}</td>
							</tr>
							<tr>
								<th class="text-left">{{ $t('cartPosition.quantity') }}</th>
								<td>{{ position.quantity }} {{ unitName }}</td>
							</tr>
						</tbody>
					</v-simple-table>
				</div>
				<v-divider/>
				<div class="pa-6">
					<h3 class="subtitle-1 mb-3">{{ $t('cart.bulkOffer.additionalScaleQuantities') }}</h3>
					<p class="body-2 mb-4">{{ $t('cart.bulkOffer.additionalScaleQuantitiesHint') }}</p>
					<v-row>
						<v-col :cols="12" :md="6">
							<AsiTextField v-model.number="model.additionalScaleQuantity1Meters"
							              :rules="model.rules('additionalScaleQuantity1Meters')"
							              :label="$t('cart.bulkOffer.additionalScaleQuantity1')"
							              :placeholder="$t('cart.bulkOffer.additionalScaleQuantity1')"
							              :suffix="$t('constants.unitMeter')"
							              type="number"
							              min="0.01"
							              step="0.01"
							              @input="sanitizeAdditionalScaleQuantity('additionalScaleQuantity1Meters')"
							              clearable/>
						</v-col>
						<v-col :cols="12" :md="6">
							<AsiTextField v-model.number="model.additionalScaleQuantity2Meters"
							              :rules="model.rules('additionalScaleQuantity2Meters')"
							              :label="$t('cart.bulkOffer.additionalScaleQuantity2')"
							              :placeholder="$t('cart.bulkOffer.additionalScaleQuantity2')"
							              :suffix="$t('constants.unitMeter')"
							              type="number"
							              min="0.01"
							              step="0.01"
							              @input="sanitizeAdditionalScaleQuantity('additionalScaleQuantity2Meters')"
							              clearable/>
						</v-col>
					</v-row>
				</div>
				<v-divider/>
				<div class="pa-6">
					<h3 class="subtitle-1 mb-3">{{ $t('cart.bulkOffer.customerDetails') }}</h3>
					<v-row>
						<SalutationInput v-model="model.salutation"/>
					</v-row>
					<v-row>
						<v-col :cols="12" :md="6">
							<AsiTextField v-model="model.firstname" :rules="model.rules('firstname')"
							              :label="$t('cart.bulkOffer.firstname')" :placeholder="$t('cart.bulkOffer.firstname')"/>
						</v-col>
						<v-col :cols="12" :md="6">
							<AsiTextField v-model="model.lastname" :rules="model.rules('lastname')"
							              :label="$t('cart.bulkOffer.lastname')" :placeholder="$t('cart.bulkOffer.lastname')"/>
						</v-col>
					</v-row>
					<v-row>
						<v-col :cols="12">
							<AsiTextField v-model="model.company"
							              :label="$t('cart.bulkOffer.company')" :placeholder="$t('cart.bulkOffer.company')"/>
						</v-col>
					</v-row>
					<v-row>
						<v-col :cols="12" :md="9">
							<AsiTextField v-model="model.street" :rules="model.rules('street')"
							              :label="$t('cart.bulkOffer.street')" :placeholder="$t('cart.bulkOffer.street')"/>
						</v-col>
						<v-col :cols="12" :md="3">
							<AsiTextField v-model="model.houseNumber" :rules="model.rules('houseNumber')"
							              :label="$t('cart.bulkOffer.houseNumber')" :placeholder="$t('cart.bulkOffer.houseNumber')"/>
						</v-col>
					</v-row>
					<v-row>
						<v-col :cols="12" :md="4">
							<AsiTextField v-model="model.zip" :rules="model.rules('zip')"
							              :label="$t('cart.bulkOffer.zip')" :placeholder="$t('cart.bulkOffer.zip')"/>
						</v-col>
						<v-col :cols="12" :md="8">
							<AsiTextField v-model="model.city" :rules="model.rules('city')"
							              :label="$t('cart.bulkOffer.city')" :placeholder="$t('cart.bulkOffer.city')"/>
						</v-col>
					</v-row>
					<v-row>
						<v-col :cols="12" :md="6">
							<AsiTextField v-model="model.email" :rules="model.rules('email')"
							              :label="$t('cart.bulkOffer.email')" :placeholder="$t('cart.bulkOffer.email')"/>
						</v-col>
						<v-col :cols="12" :md="6">
							<AsiTextField v-model="model.phone" :rules="model.rules('phone')"
							              :label="$t('cart.bulkOffer.phone')" :placeholder="$t('cart.bulkOffer.phone')"/>
						</v-col>
					</v-row>
				</div>
				<v-divider/>
				<div class="pa-6">
					<AsiTextarea v-model="model.remark" :rules="model.rules('remark')"
					             :label="$t('cart.bulkOffer.remark')" :placeholder="$t('cart.bulkOffer.remark')"/>
				</div>
			</v-form>
		</v-card-text>
	</AsiDialogFormLayout>
</template>

<script lang="ts">
	import {Component, Emit, Prop, Watch} from "vue-property-decorator";
	import AsiDialogForm from "@/components/common/AsiDialogForm.vue";
	import AsiDialogFormLayout from "@/components/common/AsiDialogFormLayout.vue";
	import AsiTextField from "@/components/common/AsiTextField";
	import AsiTextarea from "@/components/common/AsiTextarea";
	import SalutationInput from "@/components/common/SalutationInput.vue";
	import Icon from "@/plugins/icons";
	import Snackbar from "@/helpers/Snackbar";
	import {ICartShopListEntry} from "@/models/cart/CartShopModels";
	import {ICartPositionShopSimple} from "@/models/cart-position/CartPositionShopModels";
	import CartPositionBulkOfferRequestCreate from "@/models/cart-position/CartPositionBulkOfferRequestCreate";
	import {IAddressSimple} from "@/models/address/AddressModels";
	import {IUserShop} from "@/models/user/UserShopModels";
	import {Salutation, UnitOfMeasure} from "@/helpers/constants";
	import TranslatedValueHelper from "@/models/translated-value/TranslatedValueHelper";
	import EnumHelper from "@/helpers/EnumHelper";
	import VueI18n from "vue-i18n";
	import TranslateResult = VueI18n.TranslateResult;

	@Component({
		components: {SalutationInput, AsiTextarea, AsiTextField, AsiDialogFormLayout}
	})
	export default class CartPositionBulkOfferRequestDialog extends AsiDialogForm {

		@Prop({type: Object, required: true})
		public cart!: ICartShopListEntry;

		@Prop({type: Object, required: true})
		public position!: ICartPositionShopSimple;

		private model: CartPositionBulkOfferRequestCreate | null = null;
		protected icons = Icon;

		public created(): void {
			this.setModel();
		}

		private get itemName(): string | TranslateResult | null {
			return TranslatedValueHelper.get(this.position.item.name, this.$i18n.locale);
		}

		private get unitName(): string | null {
			return EnumHelper.textFromValue(UnitOfMeasure, this.position.item.salesUnitOfMeasure, true, this.position.quantity);
		}

		private get bulkOfferThreshold(): string {
			return this.position.bulkOfferThresholdMeters?.toLocaleString(this.$i18n.locale) ?? '';
		}

		private get isGuest(): boolean {
			return !this.$store.getters['user/isLoggedIn'];
		}

		@Watch('open')
		private onOpenChanged(open: boolean): void {
			if (open) {
				this.setModel();
			}
		}

		@Watch('position')
		private onPositionChanged(): void {
			this.setModel();
		}

		private setModel(): void {
			this.valid = false;
			this.model = new CartPositionBulkOfferRequestCreate();
			this.model.locale = this.$i18n.locale;
			this.model.salutation = Salutation.mr;

			if (this.isGuest) {
				this.valid = false;
				return;
			}

			const address = this.prefillAddress;
			const user = this.$store.getters['user/model'] as IUserShop | null;

			this.model.salutation = address?.salutation ?? user?.salutation ?? Salutation.mr;
			this.model.firstname = address?.firstname ?? user?.firstname ?? null;
			this.model.lastname = address?.lastname ?? user?.lastname ?? null;
			this.model.company = address?.company ?? this.$store.getters['user/modelCustomer']?.name ?? null;
			this.model.street = address?.street ?? null;
			this.model.houseNumber = address?.houseNumber ?? null;
			this.model.zip = address?.zip ?? null;
			this.model.city = address?.city ?? null;
			this.model.email = user?.username ?? null;
			this.model.phone = user?.phone ?? this.cart.oneTimePhone ?? null;
		}

		private get prefillAddress(): IAddressSimple | null {
			return this.cart.customerShippingAddress ?? this.cart.customerBillingAddress ?? null;
		}

		private sanitizeAdditionalScaleQuantity(property: 'additionalScaleQuantity1Meters' | 'additionalScaleQuantity2Meters'): void {
			if (this.model === null) return;

			const value = this.model[property];
			if (value === null || value === undefined || value.toString() === '') return;

			const numberValue = Number(value);
			if (!Number.isNaN(numberValue) && numberValue < 0) {
				this.model[property] = null;
			}
		}

		private requestSave(): void {
			if (this.loading || this.model === null || !this.valid) return;

			this.model.normalize();
			this.loading = true;
			this.$store.dispatch('cart/requestBulkOffer', {
				positionId: this.position.id,
				model: this.model,
			})
				.then(this.save)
				.catch(this.handleErrors)
				.finally(() => this.loading = false);
		}

		private requestCancel(event: Event): void {
			if (this.loading) return;

			this.loading = true;
			this.$store.dispatch('cart/declineBulkOffer', {
				cartId: this.cart.id,
				positionId: this.position.id,
			})
				.then(() => this.cancel(event))
				.catch(err => {
					this.handleErrors(err);
					Snackbar.updateError();
				})
				.finally(() => this.loading = false);
		}

		@Emit('save')
		public save(): void {
			return;
		}

		@Emit('cancel')
		public cancel(event: Event): Event {
			const form = this.$refs.form as HTMLFormElement;
			this.reset([], form);
			return event;
		}

	}
</script>
